import type {
  BulkPublisherActionBody,
  ProvisionPublisherBody,
  PublisherStatus,
} from '@planuze/admin-contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkPublisherAction,
  getPublisher,
  getPublisherActivity,
  listPublishers,
  provisionPublisher,
  setPublisherStatus,
  verifyPublisher,
} from '../lib/publishers-api.js';

/* Hooks TanStack Query da governança de publishers (ADR-0405 + ADR-0471 + ADR-0476). As
   mutações invalidam a lista `['publishers']` E o detalhe `['publisher']` — a página de
   detalhe (PUB-27) reflete a governança imediatamente. */

export function usePublishersList(status?: string) {
  return useQuery({
    queryKey: ['publishers', status ?? 'all'],
    queryFn: () => listPublishers(status),
  });
}

/** Detalhe de um publisher (ADR-0476). */
export function usePublisherDetail(id: string) {
  return useQuery({ queryKey: ['publisher', id], queryFn: () => getPublisher(id) });
}

/** Trilha de atividade de um publisher (aba Atividade, ADR-0466). Keyset por cursor
    (`initialPageParam undefined` + `getNextPageParam last.nextCursor`, molde do `use-audit`).
    `enabled` mantém a query lazy — só dispara quando a aba está ativa. */
export function usePublisherActivity(id: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['publisher', id, 'activity'],
    queryFn: ({ pageParam }) => getPublisherActivity(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });
}

/* Invalida lista + detalhe após uma mutação de governança. */
function invalidatePublishers(queryClient: ReturnType<typeof useQueryClient>): void {
  queryClient.invalidateQueries({ queryKey: ['publishers'] });
  queryClient.invalidateQueries({ queryKey: ['publisher'] });
}

export function useVerifyPublisher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => verifyPublisher(id),
    onSuccess: () => invalidatePublishers(queryClient),
  });
}

export function useSetPublisherStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; status: PublisherStatus; reason: string }) =>
      setPublisherStatus(args.id, args.status, args.reason),
    onSuccess: () => invalidatePublishers(queryClient),
  });
}

export function useBulkPublisherAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkPublisherActionBody) => bulkPublisherAction(body),
    onSuccess: () => invalidatePublishers(queryClient),
  });
}

/** Provisiona um publisher designando o owner (ADR-0483) — só super_admin. Invalida a lista. */
export function useProvisionPublisher() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionPublisherBody) => provisionPublisher(body),
    onSuccess: () => invalidatePublishers(queryClient),
  });
}
